import React from 'react';
import { Icon } from '../icons.jsx';
import {
  LizziLogo, Avatar, AvatarStack, StatusChip,
  Sidebar, UserMenu, MenuItem,
  Topbar, CommandPalette,
  ChatDock, ChatMessage, FormattedText, ChatBlock,
  ToastHost,
  ProgressBar, StatPill,
  fmtMoney, fmtPct, fmtDate, fmtDateFull,
} from '../components.jsx';
import { NewBidModal, UploadModal, NotificationsPanel, notify } from '../modals.jsx';
import { MockData } from '../mock/index.js';
import { UserManagement, MfaSettings } from './settings-security.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { supabase } from '../integrations/supabase/client';
import { updateProfile } from '../data/repo.js';


const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ===============================================
// Ajustes
// ===============================================
function SettingsScreen({ theme, setTheme, demoMode = false }) {
  const [section, setSection] = useState('appearance');
  const auth = useAuth();

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">PREFERENCIAS</div>
          <h1 className="page-title">Ajustes</h1>
          <p className="muted" style={{fontSize:14, marginTop:2}}>Configuración de la organización, apariencia, integraciones y seguridad.</p>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'220px 1fr', gap:24}}>
        <div style={{display:'flex', flexDirection:'column', gap:2}}>
          {[
            ['appearance','Apariencia','Sun'],
            ['organization','Organización','Building'],
            ['team','Equipo y roles','Users'],
            ['assistants','Asistentes y prompts','Sparkles'],
            ['knowledge','Base de conocimiento','Database'],
            ...(auth?.isAdmin ? [['ai','IA y análisis','Sparkles']] : []),
            ['integrations','Integraciones','Link'],
            ['security','Seguridad','Shield'],
          ].map(([id, label, ic]) => {
            const Ico = Icon[ic];
            return (
              <button key={id} onClick={()=>setSection(id)} className={`sidebar-link ${section===id?'active':''}`} style={{fontSize:13}}>
                <Ico size={14}/> {label}
              </button>
            );
          })}
        </div>

        <div style={{display:'flex', flexDirection:'column', gap:18}}>
          {section === 'appearance' && (
            <>
              <SettingsCard title="Tema" desc="Claro u oscuro. La preferencia se guarda en este equipo.">
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                  <ThemeOption value="dark" active={theme==='dark'} onSelect={()=>setTheme('dark')}/>
                  <ThemeOption value="light" active={theme==='light'} onSelect={()=>setTheme('light')}/>
                </div>
              </SettingsCard>
              <SettingsCard title="Densidad" desc="Más compacto reduce el padding de las tablas.">
                <div style={{display:'flex', gap:8}}>
                  {['Cómoda','Equilibrada','Compacta'].map((l,i)=>(
                    <button key={l} className={`btn ${i===1?'btn-primary':'btn-outline'}`}>{l}</button>
                  ))}
                </div>
              </SettingsCard>
              <SettingsCard title="Animaciones" desc="Respeta el sistema cuando 'prefers-reduced-motion' está activo.">
                <Toggle defaultChecked/>
              </SettingsCard>
            </>
          )}
          {section === 'organization' && (
            <SettingsCard title="Impact Hub Madrid" desc="Tenant principal de la plataforma.">
              <div style={{display:'flex', alignItems:'center', gap:14}}>
                <div style={{width:54, height:54, borderRadius:12, background:'var(--bg-3)', border:'1px solid var(--line-2)', display:'flex',alignItems:'center', justifyContent:'center'}}>
                  <span className="font-display" style={{fontSize:28}}>IH</span>
                </div>
                <div>
                  <div style={{fontSize:14, fontWeight:500}}>Impact Hub Madrid</div>
                  <div style={{fontSize:12, color:'var(--text-3)'}}>impacthub.net · CIF B-85412385 · B Corp certified</div>
                </div>
                <button className="btn btn-outline" style={{marginLeft:'auto'}}>Editar perfil</button>
              </div>
            </SettingsCard>
          )}
          {section === 'team' && (
            <>
              {!demoMode && (
                <SettingsCard title="Cuentas de usuario" desc="Alta y gestión de usuarios internos de Impact Hub. Reservado a administradores.">
                  <UserManagement />
                </SettingsCard>
              )}
              <SettingsCard title="Roles del workspace" desc="Quién puede hacer qué dentro de Lizzi.">
                <div className="card" style={{overflow:'hidden', border:'1px solid var(--line-1)'}}>
                  <table className="tbl">
                    <thead><tr><th>Rol</th><th>Permisos clave</th></tr></thead>
                    <tbody>
                      <tr><td><span className="sem sem-violet">Admin</span></td><td>Usuarios · tarifario · asistentes · KB</td></tr>
                      <tr><td><span className="sem sem-info">Redactor</span></td><td>Edita memoria · dispara asistentes · sube docs</td></tr>
                      <tr><td><span className="sem sem-warning">Revisor</span></td><td>Comenta · aprueba · devuelve a redacción</td></tr>
                      <tr><td><span className="sem sem-success">Firmante</span></td><td>Visto bueno final · presenta propuesta</td></tr>
                      <tr><td><span className="sem sem-neutral">Viewer</span></td><td>Sólo lectura</td></tr>
                    </tbody>
                  </table>
                </div>
              </SettingsCard>
            </>
          )}

          {section === 'assistants' && (
            <SettingsCard title="Modelos por defecto" desc="Aplica a los 12 asistentes salvo override individual.">
              <div style={{display:'flex',flexDirection:'column', gap:10}}>
                <SettingRow label="Modelo principal"   value="Claude Sonnet 4.5 · Anthropic"/>
                <SettingRow label="Modelo de fallback" value="GPT-4.1 · OpenAI"/>
                <SettingRow label="Temperatura"        value="0,4 (precisión sobre creatividad)"/>
                <SettingRow label="Embeddings"         value="text-embedding-3-large · 3072 dims"/>
              </div>
            </SettingsCard>
          )}
          {section === 'knowledge' && (
            <SettingsCard title="Repositorios de conocimiento" desc="Documentos indexados que alimentan a los asistentes.">
              <div style={{display:'flex', flexDirection:'column', gap:10}}>
                {[
                  ['Memorias previas',     '14 documentos', 'Memmie · Lizzie'],
                  ['Proyectos cerrados',   '128 fichas',     'Solvie'],
                  ['Plantillas internas',  '5 plantillas',   'Indie'],
                  ['Corpus metodológico',  '22 metodologías','Metie'],
                  ['Tarifario v2026.3',    '20 partidas',    'Budgie · Cronnie'],
                ].map(([n,c,a],i)=>(
                  <div key={i} style={{display:'grid', gridTemplateColumns:'1fr 120px 200px auto', gap:12, alignItems:'center', padding:'12px 14px', background:'var(--bg-2)', borderRadius:8}}>
                    <div style={{display:'flex', gap:10, alignItems:'center'}}>
                      <Icon.Database size={14} style={{color:'var(--text-3)'}}/>
                      <span style={{fontSize:13}}>{n}</span>
                    </div>
                    <span className="font-mono" style={{fontSize:11.5, color:'var(--text-3)'}}>{c}</span>
                    <span style={{fontSize:11.5, color:'var(--text-3)'}}>Usado por: {a}</span>
                    <button className="btn btn-ghost" style={{fontSize:12, padding:'4px 8px'}}>Gestionar</button>
                  </div>
                ))}
              </div>
            </SettingsCard>
          )}
          {section === 'ai' && auth?.isAdmin && (
            <AiSettings />
          )}
          {section === 'integrations' && (
            <SettingsCard title="Integraciones" desc="En producción · esta es la planificación de Fase 2.">
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
                {[
                  ['PLACE',         'Plataforma de Contratación del Estado', 'Próximamente'],
                  ['Google Drive',  'Lectura de carpetas compartidas',         'Conectado'],
                  ['Microsoft 365', 'SSO + exportación a Word',                'Próximamente'],
                  ['Stripe',        'Facturación interna',                      'No aplica'],
                ].map(([n,d,s],i)=>(
                  <div key={i} className="card card-pad" style={{padding:'14px 16px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontSize:13.5, fontWeight:500}}>{n}</span>
                      <span className={`sem ${s==='Conectado'?'sem-success':'sem-neutral'}`}>{s}</span>
                    </div>
                    <div style={{fontSize:12, color:'var(--text-3)', marginTop:6}}>{d}</div>
                  </div>
                ))}
              </div>
            </SettingsCard>
          )}
          {section === 'security' && demoMode && (
            <>
              <SettingsCard title="Sesión actual" desc="Modo demo · sin autenticación real">
                <div style={{display:'flex',alignItems:'center', gap:12}}>
                  <Avatar user={window.MockData.CURRENT_USER} size={32}/>
                  <div>
                    <div style={{fontSize:13.5}}>{window.MockData.CURRENT_USER.name}</div>
                    <div style={{fontSize:11.5, color:'var(--text-3)'}}>{window.MockData.CURRENT_USER.title}</div>
                  </div>
                  <button className="btn btn-outline" style={{marginLeft:'auto'}} onClick={()=>{ try{localStorage.removeItem('lizzi:demo')}catch(e){}; window.location.href = '/login'; }}>
                    <Icon.Logout size={13}/> Salir del modo demo
                  </button>
                </div>
              </SettingsCard>
              <SettingsCard title="Verificación en dos pasos (MFA)" desc="Disponible con una cuenta real. Inicia sesión para configurarla.">
                <div style={{fontSize:13, color:'var(--text-3)'}}>El modo demo no usa autenticación real.</div>
              </SettingsCard>
            </>
          )}
          {section === 'security' && !demoMode && (
            <>
              <SettingsCard title="Mi perfil" desc="Tu nombre y cargo. Solo puedes editar tu propio perfil; los cambios se reflejan en todo el espacio.">
                <ProfileEditor />
              </SettingsCard>
              <SettingsCard title="Sesión actual" desc="Tu cuenta autenticada en Impact Hub.">
                <div style={{display:'flex',alignItems:'center', gap:12}}>
                  <div style={{width:32, height:32, borderRadius:999, background:`hsl(${auth.profile?.avatar_hue ?? 200} 60% 45%)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:13, fontWeight:600}}>
                    {(auth.profile?.display_name || auth.user?.email || '?').slice(0,1).toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontSize:13.5}}>{auth.profile?.display_name || auth.user?.email}</div>
                    <div style={{fontSize:11.5, color:'var(--text-3)'}}>
                      {auth.user?.email}{auth.roles?.length ? ` · ${auth.roles.join(', ')}` : ''}
                    </div>
                  </div>
                  <button className="btn btn-outline" style={{marginLeft:'auto'}} onClick={async ()=>{ await auth.signOut(); window.location.href = '/login'; }}>
                    <Icon.Logout size={13}/> Cerrar sesión
                  </button>
                </div>
              </SettingsCard>
              <SettingsCard title="Verificación en dos pasos (MFA)" desc="Protege tu cuenta con una app de autenticación (TOTP).">
                <MfaSettings />
              </SettingsCard>
            </>
          )}
          {section === 'billing' && null}
        </div>
      </div>
    </div>
  );
}

function ProfileEditor() {
  const auth = useAuth();
  const [name, setName] = useState(auth.profile?.display_name || '');
  const [title, setTitle] = useState(auth.profile?.title || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(auth.profile?.display_name || '');
    setTitle(auth.profile?.title || '');
  }, [auth.profile]);

  const dirty = name !== (auth.profile?.display_name || '') || title !== (auth.profile?.title || '');

  const save = async () => {
    if (!name.trim()) {
      window.dispatchEvent(new CustomEvent('lizzi:toast', { detail: { text: 'El nombre no puede estar vacío.', icon: 'AlertTriangle' } }));
      return;
    }
    setSaving(true);
    try {
      await updateProfile(auth.user.id, { display_name: name.trim(), title: title.trim() || null });
      await auth.reloadProfile();
      window.dispatchEvent(new CustomEvent('lizzi:toast', { detail: { text: 'Perfil actualizado.', icon: 'Check' } }));
    } catch (e) {
      window.dispatchEvent(new CustomEvent('lizzi:toast', { detail: { text: 'No se pudo guardar el perfil.', icon: 'AlertTriangle' } }));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius:8, padding:'10px 12px', fontSize:13.5, color:'var(--text-1)' };

  return (
    <div style={{display:'flex', flexDirection:'column', gap:12}}>
      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
        <label style={{display:'flex', flexDirection:'column', gap:6}}>
          <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.06em', textTransform:'uppercase'}}>Nombre</span>
          <input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Nombre y apellido" style={inputStyle}/>
        </label>
        <label style={{display:'flex', flexDirection:'column', gap:6}}>
          <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.06em', textTransform:'uppercase'}}>Cargo</span>
          <input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="p. ej. Consultora sénior" style={inputStyle}/>
        </label>
      </div>
      <div style={{display:'flex', justifyContent:'flex-end'}}>
        <button className="btn btn-primary" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}

function SettingsCard({ title, desc, children }) {
  return (
    <div className="card card-pad">
      <div style={{marginBottom:14}}>
        <div style={{fontSize:14, fontWeight:500}}>{title}</div>
        <div style={{fontSize:12.5, color:'var(--text-3)', marginTop:3}}>{desc}</div>
      </div>
      {children}
    </div>
  );
}

// Selección del modelo de IA para el análisis (solo admins). Guardado en app_config.
function AiSettings() {
  const [model, setModel] = useState('sonnet');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase.from('app_config').select('value').eq('key', 'analysis_model').maybeSingle();
      if (alive && data?.value) setModel(data.value);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const choose = async (value) => {
    if (value === model || saving) return;
    const prev = model;
    setModel(value); setSaving(true);
    const { error } = await supabase.from('app_config').upsert(
      { key: 'analysis_model', value, updated_at: new Date().toISOString() },
      { onConflict: 'key' },
    );
    setSaving(false);
    if (error) { setModel(prev); notify('No se pudo guardar: ' + error.message, 'AlertTriangle'); return; }
    notify(`Modelo de análisis: ${value === 'opus' ? 'Opus (máxima calidad)' : 'Sonnet (equilibrado)'}`, 'CheckCircle');
  };

  const OPTIONS = [
    { id: 'sonnet', name: 'Claude Sonnet 5.5', tag: 'Recomendado · por defecto', desc: 'Mejor relación calidad-precio y velocidad. Recomendado para el análisis de pliegos.' },
    { id: 'opus', name: 'Claude Opus 5.5', tag: 'Máxima calidad · más caro', desc: 'Mayor profundidad de razonamiento, al doble de precio por token que Sonnet 5.5. Pensado para comparar resultados puntualmente.' },
  ];

  return (
    <SettingsCard title="Modelo de análisis de pliegos" desc="Elige qué modelo de IA usa Lizzi para generar la ficha. Afecta a todos los análisis nuevos del equipo.">
      <div style={{display:'flex', flexDirection:'column', gap:10, opacity: loading ? 0.6 : 1}}>
        {OPTIONS.map((o) => {
          const active = model === o.id;
          return (
            <button key={o.id} onClick={()=>choose(o.id)} disabled={loading || saving} style={{
              textAlign:'left', display:'flex', alignItems:'flex-start', gap:12, padding:'14px 16px', borderRadius:12, cursor:'pointer',
              background: active ? 'var(--primary-soft)' : 'var(--bg-2)',
              border: active ? '1.5px solid var(--primary)' : '1.5px solid var(--line-1)',
            }}>
              <Icon.Sparkles size={18} style={{color: active ? 'var(--primary)' : 'var(--text-3)', flexShrink:0, marginTop:1}}/>
              <div style={{flex:1, minWidth:0}}>
                <div style={{display:'flex', alignItems:'center', gap:8}}>
                  <span style={{fontSize:14, fontWeight:600}}>{o.name}</span>
                  <span style={{fontSize:10.5, color:'var(--text-3)', border:'1px solid var(--line-2)', borderRadius:999, padding:'1px 8px'}}>{o.tag}</span>
                </div>
                <div style={{fontSize:12.5, color:'var(--text-3)', marginTop:4, lineHeight:1.5}}>{o.desc}</div>
              </div>
              {active && <Icon.CheckCircle size={18} style={{color:'var(--primary)', flexShrink:0}}/>}
            </button>
          );
        })}
      </div>
      <div style={{display:'flex', alignItems:'center', gap:8, marginTop:14, fontSize:12, color:'var(--text-4)'}}>
        <Icon.Info size={14}/> El modelo usado en cada análisis queda registrado en la ficha y en «Logs de IA» para comparar calidad-precio.
      </div>
    </SettingsCard>
  );
}



function ThemeOption({ value, active, onSelect }) {
  const isDark = value === 'dark';
  return (
    <button onClick={onSelect} style={{
      padding:14, borderRadius:10, textAlign:'left',
      border: active ? '1px solid var(--line-3)' : '1px solid var(--line-1)',
      background: 'var(--bg-2)',
      display:'flex',flexDirection:'column',gap:10,
      position:'relative',
    }}>
      <div style={{height:60, borderRadius:6, background: isDark ? '#0a0a0b' : '#fbfaf7', border:'1px solid var(--line-1)', padding:8, display:'flex', flexDirection:'column', gap:6}}>
        <div style={{display:'flex',gap:4}}>
          <div style={{width:24, height:8, background: isDark?'#16161a':'#e5e3dc', borderRadius:2}}></div>
          <div style={{width:60, height:8, background: isDark?'#1c1c21':'#efede7', borderRadius:2}}></div>
        </div>
        <div style={{display:'flex',gap:4}}>
          <div style={{width:12, height:24, background: isDark?'#101012':'#ffffff', border: isDark?'1px solid #232329':'1px solid #efede7', borderRadius:2}}></div>
          <div style={{flex:1, background: isDark?'#101012':'#ffffff', border: isDark?'1px solid #232329':'1px solid #efede7', borderRadius:2}}></div>
        </div>
      </div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <span style={{fontSize:13}}>{isDark ? 'Oscuro' : 'Claro'}</span>
        {active && <Icon.Check size={14} style={{color:'var(--sem-success)'}}/>}
      </div>
    </button>
  );
}

function Toggle({ defaultChecked }) {
  const [on, setOn] = useState(!!defaultChecked);
  return (
    <button onClick={()=>setOn(!on)} style={{
      width:42, height:24, borderRadius:999,
      background: on ? 'var(--accent)' : 'var(--bg-3)',
      position:'relative', transition:'all 180ms',
      border:'1px solid var(--line-2)',
    }}>
      <span style={{
        position:'absolute', top:2, left: on ? 20 : 2,
        width:18, height:18, borderRadius:999,
        background: on ? 'var(--accent-fg)' : 'var(--text-1)',
        transition:'left 180ms',
      }}></span>
    </button>
  );
}

function SettingRow({ label, value }) {
  return (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'var(--bg-2)', borderRadius:8}}>
      <span style={{fontSize:13, color:'var(--text-2)'}}>{label}</span>
      <span style={{fontSize:13, color:'var(--text-1)', fontFamily:'JetBrains Mono'}}>{value}</span>
    </div>
  );
}

window.SettingsScreen = SettingsScreen;


export { SettingsScreen, SettingsCard, ThemeOption, Toggle, SettingRow };
